import { useState, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { callMsGraph } from './utility/msGraphApiCall'
import { loginRequest } from './authConfig'
import { type GraphData } from './utility/types'

export const useGraphData = (): [GraphData, boolean] => {
  const { instance, inProgress } = useMsal()
  const initialGraphData: GraphData = {
    mail: '',
    displayName: '',
    jobTitle: '',
    loading: true,
    error: undefined
  }

  // Default to an "empty" state.
  const [graphData, setGraphData] = useState<GraphData>(initialGraphData)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchGraphData = async () => {
      setLoading(true)
      try {
        const response = await callMsGraph()
        setGraphData({
          ...response,
          loading: false,
          error: undefined
        })
      } catch (e) {
        console.error('Error fetching Graph data:', e)
        if (e instanceof InteractionRequiredAuthError) {
          // Try to acquire token interactively for Microsoft Graph
          try {
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: instance.getActiveAccount() as AccountInfo
            })
          } catch (redirectError) {
            console.error('Failed to acquire token interactively:', redirectError)
            setGraphData({
              mail: '',
              displayName: '',
              jobTitle: '',
              loading: false,
              error: 'Authentication failed for Microsoft Graph API'
            })
          }
        } else {
          // Set error state for non-auth errors
          setGraphData({
            mail: '',
            displayName: '',
            jobTitle: '',
            loading: false,
            error: e instanceof Error ? e.message : 'Unknown error occurred'
          })
        }
      } finally {
        setLoading(false)
      }
    }

    if (inProgress === InteractionStatus.None && isEmpty(graphData)) {
      fetchGraphData()
    }
  }, [inProgress, graphData, instance])

  return [graphData, loading]
}

// Helper function to check if the data is in its initial state
function isEmpty (data: GraphData): boolean {
  return data.mail === '' && data.displayName === '' && data.jobTitle === ''
}
